import { useState, useMemo } from "react";
import type {
  ComponentPropsWithoutRef,
  ReactNode,
  PropsWithChildren,
} from "react";
import { Link } from "../../../Router/Link";
import { useLocation } from "../../../Router/state";
import { NavIcon, type NavIcons } from "../../NavIcon/NavIcon";
import { isItemActive } from "../../utils";
import {
  SubMenuAnimation,
  StyledItemWrapper,
  StyledMainItemWrapper,
  ExtendedStyledItem,
  StyledItem,
  StyledItemText,
} from "./styled";
import "@nectary/assets/icons/keyboard-arrow-down";
import "@nectary/assets/icons/keyboard-arrow-up";

export type MobileNavigationBarItemProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "title"
> & {
  to: string;
  path: string;
  subPaths: string[];
  label: ReactNode;
  icon: NavIcons;
  onClickLink?: () => void;
};

export const MobileNavigationBarItem = ({
  label,
  icon,
  to,
  path,
  subPaths,
  className,
  onClickLink,
  children,
  ...props
}: PropsWithChildren<MobileNavigationBarItemProps>) => {
  const [expanded, setExpanded] = useState(false);

  const location = useLocation();

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const isActive = useMemo(
    () => isItemActive(path, location.pathname, subPaths),
    [path, subPaths, location.pathname]
  );

  return (
    <StyledMainItemWrapper className={className} {...props}>
      {children ? (
        <>
          <StyledItemWrapper onClick={toggleExpanded}>
            <ExtendedStyledItem active={isActive}>
              <StyledItem
                $active={isActive}
                role="menuitem"
                aria-selected={isActive}
              >
                <NavIcon name={icon} />
              </StyledItem>
              <StyledItemText>{label}</StyledItemText>
            </ExtendedStyledItem>
            {expanded ? (
              <sinch-icon-keyboard-arrow-up />
            ) : (
              <sinch-icon-keyboard-arrow-down />
            )}
          </StyledItemWrapper>
          <SubMenuAnimation isOpen={expanded}>{children}</SubMenuAnimation>
        </>
      ) : (
        <Link to={to} onClick={onClickLink}>
          <StyledItemWrapper>
            <ExtendedStyledItem active={isActive}>
              <StyledItem
                $active={isActive}
                role="menuitem"
                aria-selected={isActive}
              >
                <NavIcon name={icon} />
              </StyledItem>
              <StyledItemText>{label}</StyledItemText>
            </ExtendedStyledItem>
          </StyledItemWrapper>
        </Link>
      )}
    </StyledMainItemWrapper>
  );
};
